import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Database, Download, Upload, Trash2, RefreshCw, HardDrive, Calendar,
  FileJson, AlertTriangle, CheckCircle2, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useLanguage } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LocalBackupRestore = () => {
  const { t } = useLanguage();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [backupNotes, setBackupNotes] = useState("");
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/local-backups`, { withCredentials: true });
      setBackups(response.data);
    } catch (error) {
      toast.error("Failed to load backups");
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/admin/local-backup${backupNotes ? `?notes=${encodeURIComponent(backupNotes)}` : ''}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setBackupNotes("");
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create backup");
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async (backupId) => {
    setRestoring(backupId);
    try {
      const response = await axios.post(
        `${API}/admin/local-restore/${backupId}?confirm=true`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowRestoreConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRestoring(null);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("Are you sure you want to delete this backup?")) return;
    
    try {
      await axios.delete(`${API}/admin/local-backup/${backupId}`, { withCredentials: true });
      toast.success("Backup deleted successfully");
      fetchBackups();
    } catch (error) {
      toast.error("Failed to delete backup");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Create Backup Card */}
      <Card className="border-lime-200 bg-gradient-to-r from-lime-50 to-white">
        <CardHeader>
          <CardTitle className="text-xl font-outfit font-semibold text-stone-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-lime-700" />
            Local Backup & Restore
          </CardTitle>
          <CardDescription>
            Create and manage local database backups. Backups are stored on the server.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="backup-notes">Backup Notes (Optional)</Label>
              <Input
                id="backup-notes"
                value={backupNotes}
                onChange={(e) => setBackupNotes(e.target.value)}
                placeholder="e.g., Before major update, Monthly backup"
                className="mt-1"
                data-testid="backup-notes-input"
              />
            </div>
            <Button
              onClick={createBackup}
              disabled={creating}
              className="bg-lime-700 text-white hover:bg-lime-800"
              data-testid="create-backup-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Create Backup Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Available Backups</CardTitle>
              <CardDescription>{backups.length} backup(s) available</CardDescription>
            </div>
            <Button onClick={fetchBackups} variant="outline" size="sm" data-testid="refresh-backups-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Backup Date & Time</TableHead>
                <TableHead>Filename</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-left">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                    Loading backups...
                  </TableCell>
                </TableRow>
              ) : backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                    <HardDrive className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    No backups found. Create your first backup above.
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup) => (
                  <TableRow key={backup.id} data-testid={`backup-row-${backup.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <span className="font-medium">{formatDate(backup.created_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileJson className="w-4 h-4 text-amber-500" />
                        <span className="font-mono text-sm">{backup.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-stone-600">
                      {formatFileSize(backup.file_size)}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        {Object.entries(backup.record_counts || {}).map(([collection, count]) => (
                          <div key={collection} className="text-stone-500">
                            {collection}: <span className="font-medium text-stone-700">{count}</span>
                          </div>
                        )).slice(0, 3)}
                        {Object.keys(backup.record_counts || {}).length > 3 && (
                          <span className="text-stone-400">+{Object.keys(backup.record_counts).length - 3} more</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px]">
                      <span className="text-sm text-stone-500 truncate block">
                        {backup.notes || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {backup.file_exists ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-lime-100 text-lime-700 rounded-full text-xs">
                          <CheckCircle2 className="w-3 h-3" />
                          Available
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs">
                          <AlertTriangle className="w-3 h-3" />
                          Missing
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-left">
                      <div className="flex justify-end gap-2">
                        {showRestoreConfirm === backup.id ? (
                          <div className="flex items-center gap-2 bg-amber-50 p-2 rounded-lg border border-amber-200">
                            <span className="text-xs text-amber-700">Confirm restore?</span>
                            <Button
                              onClick={() => restoreBackup(backup.id)}
                              disabled={restoring === backup.id}
                              size="sm"
                              className="bg-amber-600 text-white hover:bg-amber-700 text-xs px-2 py-1 h-auto"
                            >
                              {restoring === backup.id ? "..." : "Yes"}
                            </Button>
                            <Button
                              onClick={() => setShowRestoreConfirm(null)}
                              size="sm"
                              variant="outline"
                              className="text-xs px-2 py-1 h-auto"
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              onClick={() => setShowRestoreConfirm(backup.id)}
                              disabled={!backup.file_exists}
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              data-testid={`restore-backup-${backup.id}`}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => deleteBackup(backup.id)}
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              data-testid={`delete-backup-${backup.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Important Notes:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Restoring a backup will <strong>overwrite all current data</strong>.</li>
                <li>Create a new backup before restoring to preserve current state.</li>
                <li>Backups include: Users, Products, Vendors, Transactions, Warehouses, Categories, and Manufacturing data.</li>
                <li>Backup files are stored locally on the server.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LocalBackupRestore;
